import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { useAccountQuery } from "@/lib/queries";
import type { Account } from "@/types/domain";
import { resolveDisplayName, resolveFirstName } from "@/lib/utils";
import LoadingPage from "@/pages/LoadingPage";
import ErrorPage from "@/pages/ErrorPage";

interface AccountContextValue {
  account: Account | undefined;
  tenantId: string | undefined;
  isAdmin: boolean;
  isLoading: boolean;
  isError: boolean;
  displayName: string;
  firstName: string | null;
  email: string | undefined;
}

const AccountContext = createContext<AccountContextValue>({
  account: undefined,
  tenantId: undefined,
  isAdmin: false,
  isLoading: true,
  isError: false,
  displayName: "Guest",
  firstName: null,
  email: undefined,
});

export function AccountProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { isAuthenticated, isLoading: authIsLoading, user } = auth;

  // If the user is authenticated but the token is expired, attempt a silent sign-in to refresh it.
  useEffect(() => {
    if (!authIsLoading && isAuthenticated && user?.expired) {
      void auth.signinSilent().catch(() => auth.signinRedirect());
    }
  }, [auth, authIsLoading, isAuthenticated, user?.expired]);

  const tokenReady = isAuthenticated && !authIsLoading && !user?.expired;
  const { data: account, isLoading, isError } = useAccountQuery(tokenReady);
  const isAdmin = account?.roles.includes("admin") ?? false;

  const profile = user?.profile as Record<string, unknown> | undefined;
  const displayName = isAuthenticated ? resolveDisplayName(profile) : "Guest";
  const firstName = isAuthenticated ? resolveFirstName(profile) : null;
  const email = profile?.email as string | undefined;

  if (authIsLoading || (isAuthenticated && user?.expired) || (tokenReady && isLoading))
    return <LoadingPage />;
  if (isAuthenticated && isError)
    return (
      <ErrorPage
        title="Unable to load account"
        message="Could not retrieve your account information. Please try refreshing the page."
      />
    );

  return (
    <AccountContext.Provider
      value={{
        account,
        tenantId: account?.tenant,
        isAdmin,
        isLoading,
        isError,
        displayName,
        firstName,
        email,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount(): AccountContextValue {
  return useContext(AccountContext);
}
